import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  Attachment,
  AttachmentsService,
} from '@kinet-open-network/client/attachments/data-access';
import {
  CameraComponent,
  confirmDialog,
  toast,
} from '@kinet-open-network/client/shared/ui';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { WebcamImage } from 'ngx-webcam';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import {
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';

let ATTACHMENT_ID_COUNTER = 0;

interface GalleryProps extends FormlyFieldProps {
  accept?: string;
  multiple?: boolean;
  previewTitle?: string;
  hideCameraButton?: boolean;
  downloadDisabled?: boolean;
}

interface FileWithData extends File {
  dataUrl?: string;
}

export class GalleryItem {
  tempId?: number;
  attachment?: Attachment;
  file?: FileWithData;

  constructor(file?: FileWithData, attachment?: Attachment) {
    this.tempId = ++ATTACHMENT_ID_COUNTER;
    this.file = file;
    this.attachment = attachment;
  }

  get id() {
    return this.attachment?.id || this.tempId;
  }

  get name() {
    return this.attachment?.name || this.file?.name;
  }

  get isUploading() {
    return this.attachment === undefined;
  }

  get isUploaded() {
    return this.attachment !== undefined;
  }

  get dataUrl() {
    return this.attachment?.dataUrl || this.file?.dataUrl;
  }
}

export interface FormlySelectFieldConfig
  extends FormlyFieldConfig<GalleryProps> {}

@Component({
  selector: 'kon-gallery-type',
  standalone: true,
  imports: [
    CommonModule,
    FormlyModule,
    DropdownModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    CameraComponent,
    DialogModule,
  ],
  template: `
    <!-- <input [formControl]="formControl" /> -->
    <div class="flex items-center justify-start flex-wrap">
      @for (image of items(); track image.id) {
      <div class="h-24 w-24 relative rounded overflow-hidden mb-2 mr-2 group">
        @if(image.dataUrl){

        <div class="absolute left-0 top-0 p-0.5 z-10">
          <button
            pButton
            type="button"
            pTooltip="Zväčšiť"
            icon="pi pi-search-plus"
            class="p-button-rounded p-button-text p-button-sm p-button-primary"
            (click)="openPreview(image)"
          ></button>
        </div>

        <img
          *ngIf="image?.attachment?.mimeType?.includes('image')"
          [src]="image.dataUrl"
          [alt]="image.name"
          [title]="image.name"
          class="object-cover h-full w-full"
        />

        <div
          class="object-cover bg-white text-black border-black w-full h-full pi pi-paperclip grid content-center"
          *ngIf="!image?.attachment?.mimeType?.includes('image')"
        >
          {{ image?.attachment?.name }}
        </div>
        } @if(image.isUploading) {
        <div
          class="absolute inset-0 flex justify-center items-center bg-white/80"
        >
          <p-progressSpinner
            strokeWidth="8"
            styleClass="w-8 h-8"
          ></p-progressSpinner>
        </div>
        } @else if (!props.downloadDisabled) {
        <div
          class="absolute top-0 w-full h-full group-hover:bg-black cursor-pointer grid items-center group group-hover:bg-opacity-50"
          (click)="download(image?.attachment)"
        >
          <i
            class="pi pi-download text-white text-2xl justify-self-center opacity-0 group-hover:opacity-100 transition-opacity"
          ></i>
        </div>
        @if(!formControl.disabled) {
        <div class="absolute right-0 top-0 p-0.5">
          <button
            pButton
            type="button"
            pTooltip="Odstrániť"
            icon="pi pi-times"
            class="p-button-rounded p-button-text p-button-sm p-button-danger"
            (click)="removeImage(image)"
          ></button>
        </div>
        } }
      </div>
      } @empty { @if(loading()) {
      <div
        class="h-24 w-24 flex justify-center items-center bg-gray-50 border border-dashed border-gray-300 rounded"
      >
        <p-progressSpinner
          strokeWidth="8"
          styleClass="w-8 h-8 m-4"
        ></p-progressSpinner>
      </div>
      } @else {

      <div
        class="h-24 w-24 flex justify-center items-center bg-gray-50 border border-dashed border-gray-300 rounded"
      >
        <!-- <i class="pi pi-image text-gray-500"></i> -->
        <span class="text-gray-500 text-center">Žiadne obrázky</span>
      </div>
      } } @if(!formControl.disabled) {
      <div class="flex flex-col justify-between items-center h-24 px-2">
        <p-button
          icon="pi pi-upload"
          [outlined]="true"
          size="small"
          (click)="addImages($event)"
          [disabled]="props.multiple === false && items().length > 0"
        />
        <p-button
          *ngIf="!props.hideCameraButton"
          icon="pi pi-camera"
          [outlined]="true"
          size="small"
          (click)="cameraVisible.set(true)"
        />
      </div>
      }
    </div>

    <input
      type="file"
      [multiple]="props.multiple !== false"
      (change)="onFileChange($event)"
      class="hidden invisible"
      [accept]="props.accept"
      #fileInput
    />
    <p-dialog
      [(visible)]="cameraVisible"
      [style]="{
        width: '100vw',
        height: '100vh',
        maxHeight: '100vh',
        maxWidth: '100vw',
      }"
    >
      <kon-camera
        [visible]="cameraVisible()"
        (onSnapshot)="onCameraSnapshot($event)"
        (onClose)="onClose()"
        ngClass="fixed top-0 left-0 w-full h-full"
        [processing]="processing()"
      ></kon-camera>
    </p-dialog>

    <p-dialog
      [visible]="previewDialogVisible()"
      (visibleChange)="onPreviewDialogVisibleChange($event)"
      [modal]="true"
      [appendTo]="'body'"
      [header]="props.previewTitle ?? previewItem()?.name ?? ''"
      [maximizable]="true"
      [style]="{ width: '50rem' }"
      [breakpoints]="{ '1199px': '75vw', '575px': '90vw' }"
    >
      <ng-template pTemplate="content">
        <div class="h-full">
          <img [src]="previewImageSrc()" class="w-full h-full object-contain" />
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyGalleryTypeComponent
  extends FieldType<FieldTypeConfig<GalleryProps>>
  implements OnInit
{
  private attachmentsService = inject(AttachmentsService);
  private destroyRef = inject(DestroyRef);
  private fileInput = viewChild<ElementRef>('fileInput');
  cameraVisible = signal(false);
  processing = signal(false);
  override defaultOptions: Partial<FieldTypeConfig<GalleryProps>> = {
    props: {
      accept: 'image/*',
    },
  };

  items = signal<GalleryItem[]>([]);

  previewItem = signal<GalleryItem | null>(null);

  previewDialogVisible = computed(() => this.previewItem() !== null);

  previewImageSrc = computed(() => {
    return this.previewItem()?.dataUrl || '';
  });

  openPreview(attachment: GalleryItem) {
    this.previewItem.set(attachment);
  }

  closePreview() {
    this.previewItem.set(null);
  }

  onPreviewDialogVisibleChange(visible: boolean) {
    if (!visible) {
      this.closePreview();
    }
  }

  loading = signal<boolean>(false);

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.formControl.value),
        map<unknown, string[]>((value) => {
          if (!Array.isArray(value)) {
            if (this.props.multiple === false && typeof value === 'string') {
              return [value];
            }

            return [];
          }

          return value;
        }),
        distinctUntilChanged((previous, current) => {
          return JSON.stringify(previous) === JSON.stringify(current);
        }),
        switchMap((ids) => {
          if (ids.length === 0) {
            return of([]);
          }

          this.loading.set(true);

          return this.attachmentsService.getByIds(ids);
        }),
      )
      .subscribe({
        next: (attachments) => {
          this.items.set(
            attachments.map(
              (attachment) => new GalleryItem(undefined, attachment),
            ),
          );
          this.loading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.loading.set(false);
        },
      });
  }

  async onFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || files.length === 0) {
      return;
    }

    await Promise.all(
      Array.from(files).map(async (file) => {
        const dataUrl = await this.readFile(file);
        const galleryItem = new GalleryItem({ ...file, dataUrl });

        this.items.update((items) => [...items, galleryItem]);

        const attachment = await this.uploadFile(file.name, dataUrl);

        this.items.update((items) =>
          items.map((item) => {
            if (item === galleryItem) {
              return new GalleryItem(undefined, attachment);
            }

            return item;
          }),
        );

        // const value = this.formControl.value || [];

        // this.formControl.setValue([...value, attachment.id]);

        if (this.props.multiple === false) {
          this.formControl.setValue(attachment.id);
          this.formControl.markAsDirty();
          return;
        } else {
          const value = this.formControl.value || [];

          this.formControl.setValue([
            ...(Array.isArray(value) ? value : []),
            attachment.id,
          ]);
          this.formControl.markAsDirty();
        }
      }),
    );
    target.value = '';
  }

  async removeImage(image: GalleryItem) {
    const attachmentId = image.attachment?.id;

    if (!attachmentId) {
      toast({
        severity: 'danger',
        summary: 'Chyba',
        detail: 'Nemožno odstrániť prílohu.',
      });

      throw new Error('Nemožno odstrániť prílohu.');
    }

    confirmDialog({
      header: 'Odstrániť prílohu',
      message: 'Naozaj chcete odstrániť túto prílohu?',
      acceptLabel: 'Odstrániť',
      rejectLabel: 'Zrušiť',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await firstValueFrom(this.attachmentsService.delete(attachmentId));
          const value = this.formControl.value || [];
          this.formControl.setValue(
            typeof value === 'string'
              ? null
              : value.filter((_id: string) => _id !== attachmentId),
          );
        } catch (error) {
          toast({
            severity: 'danger',
            summary: 'Chyba',
            detail: 'Nemožno odstrániť prílohu.',
          });

          throw error;
        }
      },
    });
  }

  async addImages(event: Event) {
    this.fileInput()?.nativeElement.click();
  }

  private readFile(file: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = (e) => {
        reject(e);
      };

      reader.readAsDataURL(file);
    });
  }

  private async uploadFile(fileName: string, fileData: string) {
    return firstValueFrom(
      this.attachmentsService.create({
        data: fileData,
        name: fileName,
      }),
    );
  }

  async download(attachment?: Attachment) {
    if (!attachment) {
      return;
    }
    return await this.attachmentsService.download(
      attachment?.id,
      attachment?.name,
    );
  }

  async onCameraSnapshot(image: WebcamImage) {
    this.processing.set(true);
    const attachment = await this.uploadFile(
      `Photo_${Date.now()}`,
      image?.imageAsDataUrl,
    );

    const value = this.formControl.value || [];

    this.formControl.setValue([...value, attachment.id]);
    this.processing.set(false);
    this.onClose();
  }

  onClose() {
    this.cameraVisible.set(false);
  }
}
